import { useState, type ReactNode } from 'react';
import { Field } from '@admin/components/Form';
import { parseValidAnswers } from './parseValidAnswers';

/**
 * Tag/chip editor for answer lists. Replaces the old free-text textarea: each
 * answer is a removable chip, and a single input commits new entries on Enter,
 * comma, or blur (and splits pasted comma/newline lists). Case-insensitive
 * dedupe matches the server's sanitiser, so what you see is what gets saved.
 *
 * `highlight` flags chips that were just added by the AI assistant (applied
 * patch) so the admin can see exactly what changed after clicking "Apply".
 */
interface ChipListEditorProps {
  id: string;
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
  error?: string | null;
  required?: boolean;
  hint?: ReactNode;
  minimumHint?: string;
  testId: string;
  countTestId?: string;
  placeholder?: string;
  /** Case-insensitive set of items to visually flag as newly added. */
  highlight?: readonly string[];
}

/** Merge typed/pasted text into the list, trimming + case-insensitive dedupe. */
function mergeItems(existing: string[], raw: string): string[] {
  const seen = new Set(existing.map((s) => s.toLowerCase()));
  const out = [...existing];
  for (const candidate of parseValidAnswers(raw)) {
    const key = candidate.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(candidate);
  }
  return out;
}

export default function ChipListEditor({
  id,
  label,
  items,
  onChange,
  disabled,
  error,
  required,
  hint,
  minimumHint,
  testId,
  countTestId,
  placeholder = 'Type an answer, press Enter…',
  highlight,
}: ChipListEditorProps) {
  const [draft, setDraft] = useState('');
  const highlightSet = new Set((highlight ?? []).map((s) => s.toLowerCase()));

  function commit(raw: string) {
    const next = mergeItems(items, raw);
    if (next.length !== items.length) onChange(next);
    setDraft('');
  }

  function removeAt(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <Field
      label={label}
      htmlFor={id}
      error={error ?? null}
      required={required}
      hint={
        <>
          <strong data-testid={countTestId}>{items.length}</strong>{' '}
          {items.length === 1 ? 'entry' : 'entries'}. Type and press Enter or
          comma to add; paste a comma/newline list to add many.
          {minimumHint ? ` ${minimumHint}` : ''}
          {hint ? <> {hint}</> : null}
        </>
      }
    >
      <div
        data-testid={testId}
        className={
          'mt-1 flex flex-wrap gap-1.5 rounded border px-2 py-2 ' +
          (error ? 'border-red-400' : 'border-slate-300') +
          (disabled ? ' bg-slate-100' : ' bg-white')
        }
        onClick={() => {
          if (!disabled) document.getElementById(id)?.focus();
        }}
      >
        {items.map((item, i) => {
          const isNew = highlightSet.has(item.toLowerCase());
          return (
            <span
              key={`${item}-${i}`}
              data-testid={`${testId}-chip-${i}`}
              className={
                'inline-flex items-center gap-1 rounded px-2 py-0.5 text-sm ' +
                (isNew
                  ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-400'
                  : 'bg-slate-100 text-slate-800')
              }
            >
              {item}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(i);
                }}
                disabled={disabled}
                aria-label={`Remove ${item}`}
                data-testid={`${testId}-remove-${i}`}
                className="text-slate-500 hover:text-red-600 disabled:opacity-40"
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          id={id}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit(draft);
            } else if (
              e.key === 'Backspace' &&
              draft === '' &&
              items.length > 0
            ) {
              // Empty-field backspace removes the last chip (familiar tag UX).
              removeAt(items.length - 1);
            }
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[\n,]/.test(text)) {
              e.preventDefault();
              commit(text);
            }
          }}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={`${testId}-input`}
          className="flex-1 min-w-[8rem] bg-transparent px-1 py-0.5 text-sm text-slate-900 focus:outline-none disabled:cursor-not-allowed"
        />
      </div>
    </Field>
  );
}
